/**
 * useGameAnswers — Realtime-backed answer list for a game.
 *
 * Replaces the legacy reads of `game.answers` JSONB on the `game` row
 * (audit M8 / B7). Subscribes to `postgres_changes` on the normalized
 * `game_answer` table, scoped to a single `game_id`, and re-fetches on
 * every payload. Same trade-off as `useGamePlayers`: refetch over diff
 * keeps the hook simple and the array always in agreement with the DB.
 *
 * Subscribed table must be added to the `supabase_realtime` publication
 * (cf. migration 043).
 */

import { useEffect, useRef, useState } from "react"
import type { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js"

import { getGameAnswers } from "../../lib/supabase/game-answer"
import type { Answer } from "../../types"

export const useGameAnswers = (
  gameId: string | null,
  supabase: SupabaseClient | null
): Answer[] => {
  const [answers, setAnswers] = useState<Answer[]>([])
  const channelRef = useRef<RealtimeChannel | null>(null)

  useEffect(() => {
    if (!gameId || !supabase) {
      setAnswers([])
      return
    }

    let cancelled = false

    const refetch = async () => {
      const list = await getGameAnswers(gameId, supabase)
      if (!cancelled) setAnswers(list)
    }

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }

    refetch()

    const channel = supabase
      .channel(`game-answer-${gameId}`)
      .on(
        "postgres_changes" as never,
        {
          event: "*",
          schema: "public",
          table: "game_answer",
          filter: `game_id=eq.${gameId}`,
        },
        () => {
          refetch()
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      cancelled = true
      if (channelRef.current && supabase) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [gameId, supabase])

  return answers
}
